import useGetUserAccessories from '@/Components/Hooks/useGetUserAccessories';
import { SEARCH_DEBOUNCE_MS } from '@/constants/search';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Devices pending factory reset are "Not Ready" and must not be offered for assignment.
const isReadyToAssign = (device) =>
  device.provisioningstatus === 'Provisioned' &&
  !device.accountname &&
  device.operationalstatus !== 'TO_DO_FACTORYRESET';

export const useAssignSmartCameraHandler = (
  fetchDevices,
  hubRecord,
  setDeviceStatus,
  setFilteredAccessoriesData,
  setSelectedDevice
) => {
  const [isSmartCameraModalOpen, setIsSmartCameraModalOpen] = useState(false);
  const [readyToAssignDevices, setReadyToAssignDevices] = useState([]);
  const [searchText, setSearchText] = useState(null);
  const [selectedSmartCameraDevice, setSelectedSmartCameraDevice] = useState(null);
  const [readyToAssignloading, setReadyToAssignLoading] = useState(false);
  const userApi = useSelector((state) => state.apis.userApi);

  const {
    data: accessoriesData = [],
    isFetched: dataFetched,
    refetch,
  } = useGetUserAccessories(hubRecord);

  const handleDeviceSmartCameraSelect = (device) => {
    setSelectedSmartCameraDevice(device); // Set the selected device
  };

  // Incremented per request so late responses for an older query are ignored.
  const latestRequestRef = useRef(0);

  const handleSearchApply = (query = searchText) => {
    const requestId = ++latestRequestRef.current;
    const lowerCaseQuery = query?.toLowerCase();
    fetchDevices(null, (fetchedDevices) => {
      if (requestId !== latestRequestRef.current) return;
      const readyToAssign = (fetchedDevices ?? []).filter(isReadyToAssign);
      setReadyToAssignDevices(
        lowerCaseQuery
          ? readyToAssign.filter((device) =>
              device.deviceid?.toLowerCase().includes(lowerCaseQuery)
            )
          : readyToAssign
      );
    });
  };

  useEffect(() => {
    // null means cleared/closed; those paths fetch on their own.
    if (searchText === null) return;
    const timer = setTimeout(() => handleSearchApply(searchText), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const handleClearSearch = () => {
    setSearchText(null);
    if (isSmartCameraModalOpen) handleSearchApply('');
    // Refresh the hub's own device status for the header and device list.
    const queryParams = { filterkey: 'useruuid', filtervalue: hubRecord?.useruuid };
    fetchDevices(queryParams, (fetchedDevices) => {
      if (setDeviceStatus) {
        if (fetchedDevices?.length > 0) {
          setDeviceStatus(fetchedDevices[0].deviceconnectstatus?.toLowerCase());
          setSelectedDevice((prev) => {
            // for updating popup modal
            return {
              ...prev,
              deviceuuid: fetchedDevices[0].deviceuuid,
              lastconnecttime: fetchedDevices[0].lastconnecttime_utc,
              lastdisconnecttime: fetchedDevices[0].lastdisconnecttime_utc,
            };
          });
          setFilteredAccessoriesData((prev) => {
            // for updating list...
            const matchedItem = prev.findIndex(
              (item) => item.deviceuuid === fetchedDevices[0].deviceuuid
            );
            if (matchedItem > -1) {
              const newData = [...prev];
              newData[matchedItem] = {
                ...newData[matchedItem],
                lastconnecttime: fetchedDevices[0].lastconnecttime_utc,
                lastdisconnecttime: fetchedDevices[0].lastdisconnecttime_utc,
              };
              return [...newData];
            }
            return prev;
          });
        }
      }
    });
  };

  // Function to handle the modal opening
  const handleAssignSmartCamera = () => {
    setIsSmartCameraModalOpen(true);
    handleAssignSmartCameraList();
  };

  // Function to close the modal
  const closeModal = () => {
    setIsSmartCameraModalOpen(false);
    setSearchText(null); // Clears search input
    setSelectedSmartCameraDevice(null);
  };
  const handleAssignSmartCameraList = () => {
    const requestId = ++latestRequestRef.current;
    setReadyToAssignLoading(true);
    // Call fetchDevices and handle the data
    fetchDevices(null, (fetchedDevices) => {
      setReadyToAssignLoading(false);
      if (requestId !== latestRequestRef.current) return;
      // Filter devices ready for assignment do not include those devices which are set for TO_DO_FACTORYRESET
      const readyToAssign = (fetchedDevices ?? []).filter(isReadyToAssign);
      if (readyToAssign.length === 0) {
        console.warn('No devices are ready for assignment');
      }
      // Always replace, so an earlier search result or assigned device is not left on screen.
      setReadyToAssignDevices(readyToAssign);
    });
  };

  const assignDevice = (
    hubRecord,
    device,
    trans,
    fetchDevices,
    setHasDevice,
    closeModal,
    refetch,
    queryParams,
    setDeviceLocation,
    setDeviceStatus,
    setStatusTitle,
    setHeaderRefreshKey
  ) => {
    const errorCallback = (error) => {
      console.error('Error assigning device:', error);
      // message.error("Cannot Assign Device to User: " + error.message);
    };
    const successCallback = () => {
      // message.success("Device assigned successfully");
      setHasDevice(true);
      closeModal();
      // Fetch updated list of devices after successful assignment
      fetchDevices(queryParams, (fetchedDevices) => {
        if (fetchedDevices?.length > 0) {
          const assignedDevice = fetchedDevices.find((d) => d.deviceuuid === device.deviceuuid);
          if (assignedDevice) {
            setDeviceStatus(assignedDevice.deviceconnectstatus.toLowerCase());
            setDeviceLocation(assignedDevice.location);
            setStatusTitle('Smart Camera');
            setHeaderRefreshKey('assign_device', assignedDevice.deviceid);
          }
        }
      });
      refetch();
    };
    try {
      const userAssociation = {
        deviceuuid: device.deviceuuid,
      };
      userApi.associateDeviceToUser(
        hubRecord.useruuid,
        userAssociation,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  return {
    isSmartCameraModalOpen,
    setIsSmartCameraModalOpen,
    readyToAssignDevices,
    searchText,
    setSearchText,
    selectedSmartCameraDevice,
    setSelectedSmartCameraDevice,
    readyToAssignloading,
    handleDeviceSmartCameraSelect,
    handleClearSearch,
    handleAssignSmartCamera,
    closeModal,
    handleAssignSmartCameraList,
    assignDevice,
    accessoriesData,
    dataFetched,
    refetch,
  };
};
